import { type DocumentSnapshot, type QueryDocumentSnapshot } from 'firebase/firestore';
import type { Event, EventStatus, RSVP } from '@/types';
import { DEFAULT_CREDIT_BOOKING_FEE_PENCE } from '@/lib/platform-booking-fee';
import { getFullName } from '@/lib/utils';

/** Same defaults as {@link MyRSVPDashboardPage} when reading Firestore so booking fee / recovery flags match RSVP. */
function mapCreditCostRecoveryFromFirestore(raw: unknown): NonNullable<Event['creditCostRecovery']> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as { enabled?: boolean; amountPence?: unknown };
    return {
      enabled: Boolean(o.enabled),
      amountPence:
        typeof o.amountPence === 'number' ? o.amountPence : DEFAULT_CREDIT_BOOKING_FEE_PENCE,
    };
  }
  return { enabled: false, amountPence: DEFAULT_CREDIT_BOOKING_FEE_PENCE };
}

/** Sentence-style label for the event status (first letter capitalised). */
export function formatEventStatusLabel(status: EventStatus): string {
  if (!status) {
    return '';
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function mapEventData(eventDoc: DocumentSnapshot, unitId: string): Event {
  const data: any = eventDoc.data();
  return {
    id: eventDoc.id,
    unitId,
    name: data?.name,
    date: data?.date?.toDate?.() || new Date(),
    location: data?.location,
    venueId: data?.venueId,
    costPerPerson: data?.costPerPerson,
    questionnaire: data?.questionnaire || [],
    courses: data?.courses || [],
    tickets: data?.tickets || [],
    ticketMode: data?.ticketMode === 'multiTenant' ? 'multiTenant' : 'mealChoice',
    currentAttendees: data?.currentAttendees || 0,
    maxCapacity: data?.maxCapacity,
    waitlistEnabled: data?.waitlistEnabled || false,
    waitlist: data?.waitlist || [],
    rsvpDeadline: data?.rsvpDeadline?.toDate?.() || new Date(),
    paymentDeadline: data?.paymentDeadline?.toDate?.() || data?.date?.toDate?.() || new Date(),
    eventStatus: data?.eventStatus || 'draft',
    dresscode: data?.dresscode,
    customInstructions: data?.customInstructions,
    isPublic: data?.isPublic,
    requirePrefixRankUnitInfo: data?.requirePrefixRankUnitInfo ?? true,
    useCustomPrefixes: Boolean(data?.useCustomPrefixes),
    customPrefixes: Array.isArray(data?.customPrefixes)
      ? data.customPrefixes.filter((p: unknown) => typeof p === 'string' && p.trim() !== '')
      : [],
    createdBy: data?.createdBy,
    createdAt: data?.createdAt?.toDate?.() || new Date(),
    guestCoversCreditCost: data?.guestCoversCreditCost ?? false,
    creditCostRecovery: mapCreditCostRecoveryFromFirestore(data?.creditCostRecovery),
    creditsPerRsvp: data?.creditsPerRsvp,
    creditBookingFeePence:
      typeof data?.creditBookingFeePence === 'number' && Number.isFinite(data.creditBookingFeePence)
        ? data.creditBookingFeePence
        : undefined,
  } as Event;
}

export function isActiveRsvpData(data: { deletedAt?: unknown } | undefined): boolean {
  return data != null && data.deletedAt == null;
}

/**
 * Raw RSVP data for a guest who should be seated (table planner): not soft-deleted, not an
 * apology, not cancelled or declined.
 */
export function isSeatableRsvpData(
  data: { deletedAt?: unknown; apologies?: unknown; status?: unknown } | undefined,
): boolean {
  if (!isActiveRsvpData(data)) return false;
  return data!.apologies !== true && data!.status !== 'cancelled' && data!.status !== 'declined';
}

/** First RSVP doc without soft-delete (deleteRsvpSafe retention). */
export function findFirstActiveRsvpDoc(
  docs: ReadonlyArray<DocumentSnapshot | QueryDocumentSnapshot>,
): DocumentSnapshot | QueryDocumentSnapshot | undefined {
  return docs.find((docSnap) => isActiveRsvpData(docSnap.data()));
}

/** RSVP documents soft-deleted by admin (deleteRsvpSafe) are hidden from normal lists. */
export function mapRsvpData(rsvpDoc: DocumentSnapshot | QueryDocumentSnapshot): RSVP | null {
  const data: any = rsvpDoc.data();
  if (!isActiveRsvpData(data)) {
    return null;
  }
  return {
    id: rsvpDoc.id,
    eventId: data?.eventId,
    userId: data?.userId,
    name: data?.name,
    firstName: data?.firstName,
    lastName: data?.lastName,
    rank: data?.rank,
    rankShortName: data?.rankShortName,
    additionalRank: data?.additionalRank,
    unit: data?.unit,
    email: data?.email,
    responses: data?.responses,
    status: data?.status,
    apologies: data?.apologies,
    invoiceId: data?.invoiceId,
    paid: data?.paid,
    invitedBy: data?.invitedBy,
    inviterPaysFor: data?.inviterPaysFor,
    checkedIn: data?.checkedIn,
    checkInTime: data?.checkInTime?.toDate?.(),
    specialRequests: data?.specialRequests,
    plusOneAllowed: data?.plusOneAllowed,
    customPrice: data?.customPrice != null ? Number(data.customPrice) : undefined,
    createdAt: data?.createdAt?.toDate?.() || new Date(),
    updatedAt: data?.updatedAt?.toDate?.() || new Date(),
  } as RSVP;
}

export function isActiveInvoiceData(data: { deletedAt?: unknown } | undefined): boolean {
  return data != null && data.deletedAt == null;
}

/** Active RSVPs only; soft-deleted docs from deleteRsvpSafe are excluded. */
export function mapActiveRsvpsFromDocs(
  docs: Array<DocumentSnapshot | QueryDocumentSnapshot>,
): RSVP[] {
  return docs.map(mapRsvpData).filter((rsvp): rsvp is RSVP => rsvp != null);
}

/**
 * Matches backend deleteRsvpSafe / currentAttendees maintenance: confirmed, pending,
 * or unset status; excludes apologies, declined, cancelled, and waitlist.
 */
export function isAttendeeCountingRsvp(rsvp: Pick<RSVP, 'apologies' | 'status'>): boolean {
  if (rsvp.apologies === true || rsvp.status === 'declined' || rsvp.status === 'cancelled') {
    return false;
  }
  return rsvp.status === 'confirmed' || rsvp.status === 'pending' || !rsvp.status;
}

/**
 * Headcount for one RSVP: sum of `responses.Tickets[].qty` when present (multi-tenant carts can
 * hold several tickets per booking), else 1 (meal-choice/legacy RSVPs represent a single attendee).
 */
export function getRsvpAttendeeQuantity(rsvp: Pick<RSVP, 'responses'>): number {
  const lines = Array.isArray(rsvp.responses?.Tickets) ? (rsvp.responses.Tickets as Array<{ qty?: number }>) : [];
  if (lines.length === 0) {
    return 1;
  }
  const total = lines.reduce((sum, line) => sum + Math.max(0, Number(line?.qty) || 0), 0);
  return total > 0 ? total : 1;
}

/** Live attendee count from RSVP rows (same rules as event.currentAttendees); qty-aware for multi-tenant bookings. */
export function countAttendeeRsvps(rsvps: RSVP[]): number {
  return rsvps.filter(isAttendeeCountingRsvp).reduce((sum, r) => sum + getRsvpAttendeeQuantity(r), 0);
}

/** Delta for event.currentAttendees when toggling apologies on an existing RSVP. */
export function computeAttendeeDeltaForApologyToggle(
  rsvp: Pick<RSVP, 'apologies' | 'status'>,
  newApologyValue: boolean,
): number {
  if (newApologyValue && !rsvp.apologies) {
    if (!['waitlist', 'declined', 'cancelled'].includes(rsvp.status ?? '')) {
      return -1;
    }
  } else if (!newApologyValue && rsvp.apologies && rsvp.status === 'declined') {
    return 1;
  }
  return 0;
}

/** True when saving a ticket should increment currentAttendees after clearing apologies. */
export function shouldIncrementAttendeesWhenRemovingApology(
  rsvp: Pick<RSVP, 'apologies' | 'status'>,
): boolean {
  return computeAttendeeDeltaForApologyToggle(rsvp, false) === 1;
}

export type AttendeeStatSummary = {
  attending: number;
  total: number;
  apologyCount: number;
};

/** Event dashboard attendee card: attending count, RSVP total, and apologies. */
export function buildAttendeeStatSummary(rsvps: RSVP[]): AttendeeStatSummary {
  return {
    attending: countAttendeeRsvps(rsvps),
    total: rsvps.length,
    apologyCount: rsvps.filter((r) => r.apologies).length,
  };
}

/**
 * Legacy event dashboard metric (pre-fix): all RSVPs except apologies.
 * Still counts waitlist and declined guests without the apologies flag.
 */
export function countNonApologyRsvps(rsvps: RSVP[]): number {
  return rsvps.filter((r) => !r.apologies).length;
}

/**
 * Returns the corrected currentAttendees value when stored count has drifted,
 * or null when no sync write is needed.
 */
export function resolveCurrentAttendeesSyncTarget(
  rsvps: RSVP[],
  storedCurrentAttendees: number,
  eventStatus: Event['eventStatus'] | undefined,
): number | null {
  if (!eventStatus || eventStatus === 'interest-only') {
    return null;
  }
  const liveCount = countAttendeeRsvps(rsvps);
  return liveCount === storedCurrentAttendees ? null : liveCount;
}

export type InvitationEmailConflict =
  | { kind: 'duplicate-in-batch'; email: string }
  | { kind: 'already-on-event'; email: string };

/**
 * Returns the first invitation email conflict, or null when all guest emails are allowed.
 * Soft-deleted RSVPs are ignored so removed guests can be re-invited.
 */
export function findInvitationEmailConflict(
  guestEmails: string[],
  existingRsvpDocs: Array<DocumentSnapshot | QueryDocumentSnapshot>,
): InvitationEmailConflict | null {
  const normalizedEmails = guestEmails.map((email) => email.trim().toLowerCase());
  const duplicateInBatch = normalizedEmails.find(
    (email, index) => normalizedEmails.indexOf(email) !== index,
  );
  if (duplicateInBatch) {
    return { kind: 'duplicate-in-batch', email: duplicateInBatch };
  }

  const activeEmails = new Set<string>();
  for (const existingDoc of existingRsvpDocs) {
    const existingRsvp = mapRsvpData(existingDoc);
    if (!existingRsvp?.email) {
      continue;
    }
    activeEmails.add(existingRsvp.email.trim().toLowerCase());
  }

  for (const email of normalizedEmails) {
    if (activeEmails.has(email)) {
      return { kind: 'already-on-event', email };
    }
  }

  return null;
}

/**
 * Invited guest whose host pays for their ticket (matches My RSVP dashboard).
 * When true, this person does not owe on the host's invoice for their own seat.
 */
export function isInviteeCoveredByInviter(rsvp: RSVP | null | undefined): boolean {
  if (!rsvp || rsvp.apologies === true) {
    return false;
  }
  const invitedBy = typeof rsvp.invitedBy === 'string' ? rsvp.invitedBy.trim() : '';
  if (!invitedBy) {
    return false;
  }
  return rsvp.inviterPaysFor !== false;
}

/**
 * Subtotal for one RSVP: RSVP-level customPrice, then stored ticket line prices (custom per guest),
 * then current event ticket prices, then legacy costPerPerson.
 */
export function computeRsvpSubtotalForInvoice(rsvp: RSVP, event: Event): number {
  if (rsvp.customPrice !== undefined && rsvp.customPrice !== null && Number.isFinite(rsvp.customPrice)) {
    return Number(rsvp.customPrice);
  }
  if (event.tickets?.length && Array.isArray(rsvp.responses?.Tickets)) {
    let sum = 0;
    for (const line of rsvp.responses.Tickets as { id?: string; qty?: number; price?: number }[]) {
      const qty = Number(line?.qty || 0);
      if (!qty || !line?.id) {
        continue;
      }
      const ticketType = event.tickets.find((t) => t.id === line.id);
      const storedPrice =
        line.price !== undefined && line.price !== null && Number.isFinite(Number(line.price))
          ? Number(line.price)
          : null;
      const eventPrice = ticketType ? Number(ticketType.price || 0) : null;
      const price = storedPrice ?? eventPrice ?? 0;
      sum += qty * price;
    }
    return sum;
  }
  return Number(event.costPerPerson || 0);
}

/** Expected amount due from RSVP pricing (customPrice, stored ticket lines, event tickets, costPerPerson). */
export function getRsvpExpectedAmount(rsvp: RSVP, event: Event | null): number {
  if (!event) return 0;
  return computeRsvpSubtotalForInvoice(rsvp, event);
}

/**
 * Matches backend announcement "unpaid" recipients: owes money, excluding bank transfer pending verification,
 * £0 tickets, and invitees whose host pays.
 */
export function shouldIncludeRsvpInUnpaidAnnouncementGroup(
  rsvp: RSVP,
  event: Event | null,
  invoiceById: Map<string, { status?: string; outstandingBalance?: number }>
): boolean {
  if (rsvp.apologies === true || rsvp.status === 'declined' || rsvp.status === 'cancelled') {
    return false;
  }

  if (isInviteeCoveredByInviter(rsvp)) {
    return false;
  }

  if (event && getRsvpExpectedAmount(rsvp, event) === 0) {
    return false;
  }

  if (rsvp.invoiceId) {
    const inv = invoiceById.get(rsvp.invoiceId);
    if (!inv) {
      return rsvp.paid !== true;
    }
    if (inv.status === 'pending_verification') {
      return false;
    }
    return (inv.outstandingBalance ?? 0) > 0;
  }

  return rsvp.paid !== true;
}

export interface TableLayout {
  id?: string;
  tableName: string;
  seats: number;
}

export function normalizeRsvpVerificationField(value: string | undefined | null): string {
  return (value ?? '').toLowerCase().trim();
}

export type RsvpIdentityVerificationInput = {
  name: string;
  email: string;
  rank: string;
};

/** Compare guest-entered verification fields against stored RSVP identity (case-insensitive name/email). */
export function matchesRsvpIdentity(
  input: RsvpIdentityVerificationInput,
  rsvp: Pick<RSVP, 'name' | 'firstName' | 'lastName' | 'email' | 'rank'>,
  options: { requiresPrefix: boolean },
): boolean {
  const storedName = getFullName(rsvp);
  const nameMatch =
    normalizeRsvpVerificationField(input.name) === normalizeRsvpVerificationField(storedName);
  const emailMatch =
    normalizeRsvpVerificationField(input.email) === normalizeRsvpVerificationField(rsvp.email);
  const rankMatch = options.requiresPrefix ? input.rank === rsvp.rank : true;
  return nameMatch && emailMatch && rankMatch;
}



